/**
 * A list of equipment, drawn as a table — the one renderer, used by /cart («Your spec») and by the
 * setup builder («Your setup»).
 *
 * One renderer on purpose: the two lists are shown the same way, and a second implementation would
 * drift from this one the first time a column changes. WHICH list is drawn is the caller's to say:
 * every call takes a store (assets/spec-store.js), because the spec and the builder's working list
 * are deliberately separate (owner, 2026-08-06).
 *
 * Columns follow the layout the owner picked: remove · product · price · quantity · subtotal, with
 * the totals card beside the table. Below 768px the row folds into a card — the picture and the
 * name on top, the stepper and the line total under them — and the column headings step aside for
 * per-cell labels.
 *
 * Every mutation goes back through the store and fires its event; the page that owns the container
 * redraws on it. Nothing here holds state except which briefs are unfolded — that is where the
 * visitor is looking, not part of the list.
 */
(function () {
  // Storefront root of the locale being browsed — `/` or `/de/`, always trailing-slashed.
  // A bare '/products/…' fetch answers from the primary locale, so a visitor in /de would get
  // English titles and the default market's prices.
  var localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (!window.BQSpecStore) return;

  var t = window.BQSpecStore.t;

  // '<store key>|<line key>' → is its customization brief unfolded. Outside the lists on purpose:
  // they are rebuilt whole on every change, and a field that folded itself away mid-sentence is
  // unusable. Keyed by store as well, so the same product in the spec and in the builder does not
  // share one open/closed state.
  var noteOpen = {};

  function noteKey(store, key) {
    return store.key + '|' + key;
  }

  function icon(name, fallbackText) {
    var tpl = document.querySelector('[data-spec-icon-' + name + ']');
    if (tpl && tpl.content) return tpl.content.cloneNode(true);
    return document.createTextNode(fallbackText);
  }

  /** What one line costs each — a made-to-order position is priced FROM its base build. */
  function unitPrice(store, line) {
    if (typeof line.price !== 'number') return '—';
    var value = store.money(line.price);
    return line.customizable ? t('price_from', 'from') + ' ' + value : value;
  }

  function lineTotal(store, line) {
    if (typeof line.price !== 'number') return '—';
    var value = store.money(line.price * (line.qty || 1));
    return line.customizable ? t('price_from', 'from') + ' ' + value : value;
  }

  /** The sum of a list, with «from» while anything in it is unpriced or made-to-order. */
  function totalLabel(store) {
    var owner = store || window.BQSpec;
    var totals = owner.totals();
    return (totals.from ? t('price_from', 'from') + ' ' : '') + owner.money(totals.subtotal);
  }

  /* --------------------------------------------------------------- one line */

  // No caption inside the cell: the column heading carries it on a desktop, and on a phone the
  // shape of the row says which number is which without three words of help (owner, 2026-08-06).
  function buildCell(className, value) {
    var cell = document.createElement('div');
    cell.className = className;

    var text = document.createElement('span');
    text.className = 'spec-row__value';
    text.textContent = value;

    cell.appendChild(text);
    return cell;
  }

  function buildStepper(store, key, line) {
    var wrap = document.createElement('div');
    wrap.className = 'spec-qty';

    var floor = line.moq || 1;

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'spec-qty__btn';
    minus.setAttribute('aria-label', t('less', 'Fewer'));
    minus.appendChild(icon('minus', '−'));
    // The minus stops AT the supplier minimum rather than walking through it: a quantity below MOQ
    // is one we cannot ship. Dropping the position entirely is the × on the left.
    minus.disabled = (line.qty || 1) <= floor;

    var value = document.createElement('span');
    value.className = 'spec-qty__value';
    value.textContent = String(line.qty || 1);

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'spec-qty__btn';
    plus.setAttribute('aria-label', t('more_qty', 'More'));
    plus.appendChild(icon('plus', '+'));

    minus.addEventListener('click', function () { store.bump(key, -1); });
    plus.addEventListener('click', function () { store.bump(key, 1); });

    // The minimum is not printed under the stepper any more (owner, 2026-08-06) — a stopped minus
    // says «no lower» by itself. It stays as the control's own title, for whoever wonders why.
    if (floor > 1) wrap.title = t('moq_hint', 'MOQ =') + ' ' + floor;

    wrap.appendChild(minus);
    wrap.appendChild(value);
    wrap.appendChild(plus);
    return wrap;
  }

  // handle -> the product's variants, as the catalog last answered. One read per product per page:
  // the panel is opened far more often than a catalog changes under it.
  var variantCache = {};

  // '<store key>|<line key>' -> is its size panel open. Outside the rows for the same reason the
  // briefs are: the table is rebuilt whole on every change, and a panel that shut itself because a
  // price arrived — or because the quantity next to it moved — is a panel nobody can use.
  var varOpen = {};

  /**
   * The size of a position, and the way to change it without leaving the list (owner, 2026-08-06).
   *
   * Closed it is the line that was there before — «20×15 vintage gold» — with a chevron. Open it is
   * every size the product has, priced, with the one in the list marked: choosing between sizes is
   * choosing between prices, and sending somebody back to the product page to do it is how a list
   * gets abandoned. The sizes are read from `/products/<handle>.js` when the panel is first opened,
   * so a list of forty positions costs no requests until one is questioned.
   */
  function buildVariantPicker(store, key, line) {
    var open = noteKey(store, key);

    var wrap = document.createElement('div');
    wrap.className = 'spec-var';

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'spec-var__toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', t('change_variant', 'Change the option'));

    var label = document.createElement('span');
    label.className = 'spec-var__current';
    label.textContent = line.variant_title || t('change_variant', 'Change the option');

    var chevron = document.createElement('span');
    chevron.className = 'spec-var__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.appendChild(icon('chevron', '▾'));

    toggle.appendChild(label);
    toggle.appendChild(chevron);

    var panel = document.createElement('div');
    panel.className = 'spec-var__panel';
    panel.hidden = true;

    function draw(variants) {
      panel.innerHTML = '';
      if (!variants || !variants.length) {
        var none = document.createElement('p');
        none.className = 'spec-var__state';
        none.textContent = t('unavailable', 'No longer available');
        panel.appendChild(none);
        return;
      }

      variants.forEach(function (variant) {
        var current = String(variant.id) === String(line.variant_id);
        var option = document.createElement('button');
        option.type = 'button';
        option.className = 'spec-var__option';
        option.setAttribute('aria-current', String(current));

        var name = document.createElement('span');
        name.className = 'spec-var__name';
        name.textContent = variant.title;

        var price = document.createElement('span');
        price.className = 'spec-var__price';
        price.textContent = (line.customizable ? t('price_from', 'from') + ' ' : '') +
          store.money(variant.price);

        option.appendChild(name);
        option.appendChild(price);
        // Choosing the one already chosen is just closing the panel.
        option.addEventListener('click', function () {
          setOpen(false);
          if (!current) store.setVariant(key, variant);
        });
        panel.appendChild(option);
      });
    }

    function load() {
      var handle = line.handle;
      if (variantCache[handle]) {
        draw(variantCache[handle]);
        return;
      }

      var loading = document.createElement('p');
      loading.className = 'spec-var__state';
      loading.textContent = '…';
      panel.innerHTML = '';
      panel.appendChild(loading);

      fetch(localeRoot + 'products/' + encodeURIComponent(handle) + '.js')
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (product) {
          variantCache[handle] = (product && product.variants) || [];
          if (!panel.hidden) draw(variantCache[handle]);
        })
        .catch(function () { draw([]); });
    }

    function setOpen(next) {
      varOpen[open] = next;
      panel.hidden = !next;
      toggle.setAttribute('aria-expanded', String(next));
      if (next) load();
    }

    toggle.addEventListener('click', function () { setOpen(panel.hidden); });

    wrap.appendChild(toggle);
    wrap.appendChild(panel);

    // A redraw while it was open puts it back open, with what the catalog already told us.
    if (varOpen[open]) setOpen(true);

    return wrap;
  }

  /**
   * The customization brief: a folded field under a made-to-order line. The catalog price buys the
   * standard build; what is actually wanted (size, finish, voltage, branding) is the part we have
   * to quote, and asking for it next to the position is the difference between a quote and a round
   * of email.
   *
   * It unfolds like a question in the FAQ — same chevron, same turn — and says which of three
   * states it is in rather than naming itself twice. Typing writes straight to storage and redraws
   * NOTHING: see spec-store.setNote.
   */
  function buildNote(store, key, line) {
    var open = noteKey(store, key);

    var wrap = document.createElement('div');
    wrap.className = 'spec-row__note';

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'spec-note__toggle';
    toggle.setAttribute('aria-label', t('note_toggle', 'Customization requirements'));

    // The mark says which of the two closed states this is: a pink «!» while the brief is empty
    // (we need something from you), a tick once it is written.
    var mark = document.createElement('span');
    mark.className = 'spec-note__mark';
    mark.setAttribute('aria-hidden', 'true');

    var label = document.createElement('span');
    label.className = 'spec-note__label';

    var chevron = document.createElement('span');
    chevron.className = 'spec-note__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.appendChild(icon('chevron', '▾'));

    toggle.appendChild(mark);
    toggle.appendChild(label);
    toggle.appendChild(chevron);

    var field = document.createElement('textarea');
    field.className = 'input spec-note__field';
    field.rows = 3;
    field.value = line.note || '';
    field.placeholder = t('note_placeholder', 'Size, finish, voltage, branding…');
    field.setAttribute('aria-label', t('note_toggle', 'Customization requirements'));

    /** Three states, one line: open · closed and written · closed and still empty. */
    function syncToggle() {
      var current = store.line(key);
      var filled = ((current && current.note) || '').trim() !== '';
      var isOpen = !!noteOpen[open];

      toggle.setAttribute('data-state', isOpen ? 'open' : (filled ? 'filled' : 'empty'));
      mark.textContent = isOpen ? '' : (filled ? '✓' : '!');
      label.textContent = isOpen
        ? t('note_collapse', 'Collapse')
        : (filled
          ? t('note_filled', 'Customization brief filled')
          : t('note_empty', 'This product needs information from you'));
    }

    function setOpen(next) {
      noteOpen[open] = next;
      field.hidden = !next;
      toggle.setAttribute('aria-expanded', String(next));
      syncToggle();
    }

    toggle.addEventListener('click', function () {
      setOpen(!noteOpen[open]);
      if (noteOpen[open]) field.focus();
    });

    field.addEventListener('input', function () {
      store.setNote(key, field.value);
      syncToggle();
    });

    // Folded until asked for — a list with a dozen made-to-order lines would open as a wall of
    // empty boxes. The ask is carried by the mark on the toggle instead.
    setOpen(!!noteOpen[open]);

    wrap.appendChild(toggle);
    wrap.appendChild(field);
    return wrap;
  }

  function buildRow(store, key) {
    var line = store.line(key);
    var row = document.createElement('div');
    row.className = 'spec-row';
    row.setAttribute('data-spec-key', key);

    var title = line.title || store.titleize(line.handle);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'spec-row__remove icon-button';
    remove.setAttribute('aria-label', t('remove', 'Remove') + ': ' + title);
    remove.appendChild(icon('close', '×'));
    remove.addEventListener('click', function () { store.remove(key); });

    var media = document.createElement(line.url ? 'a' : 'span');
    media.className = 'spec-row__media';
    if (line.url) {
      media.href = line.url;
      media.setAttribute('aria-hidden', 'true');
      media.setAttribute('tabindex', '-1');
    }
    if (line.image) {
      var img = document.createElement('img');
      img.src = line.image;
      img.alt = '';
      img.loading = 'lazy';
      media.appendChild(img);
    }

    var info = document.createElement('div');
    info.className = 'spec-row__info';

    var name = document.createElement(line.url ? 'a' : 'span');
    name.className = 'spec-row__title';
    if (line.url) name.href = line.url;
    name.textContent = title;
    info.appendChild(name);

    if (line.variant_id) info.appendChild(buildVariantPicker(store, key, line));
    else if (line.variant_title) {
      var variant = document.createElement('span');
      variant.className = 'spec-row__variant';
      variant.textContent = line.variant_title;
      info.appendChild(variant);
    }

    if (line.missing) {
      var gone = document.createElement('span');
      gone.className = 'spec-row__missing';
      gone.textContent = t('unavailable', 'No longer available');
      info.appendChild(gone);
    }

    var price = buildCell('spec-row__price', unitPrice(store, line));

    var qty = document.createElement('div');
    qty.className = 'spec-row__qty';
    qty.appendChild(buildStepper(store, key, line));

    var sub = buildCell('spec-row__sub', lineTotal(store, line));

    row.appendChild(remove);
    row.appendChild(media);
    row.appendChild(info);
    row.appendChild(price);
    row.appendChild(qty);
    row.appendChild(sub);
    // …and the brief, for a position we cannot quote from the catalog alone. Kept for a line that
    // merely CARRIES a note too: a product that lost the flag must not swallow what was written.
    if (line.customizable || (line.note || '').trim()) row.appendChild(buildNote(store, key, line));

    return row;
  }

  /* ------------------------------------------------------------- the table */

  /**
   * Draw a list into `container`.
   *
   * `options.store` — which list (defaults to the spec). `options.groups` — [{ title, keys }] — is
   * how the builder keeps a setup filed under its zones. Without it the list is flat, which is what
   * /cart shows: a spec picked off product pages has no venue plan behind it to file it under.
   */
  function render(container, options) {
    if (!container) return;
    var opts = options || {};
    var store = opts.store || window.BQSpec;
    container.innerHTML = '';

    var keys = Object.keys(store.items());
    if (!keys.length) return;

    var groups = opts.groups && opts.groups.length
      ? opts.groups
      : [{ title: '', keys: keys }];

    var table = document.createElement('div');
    table.className = 'spec-table';

    var head = document.createElement('div');
    head.className = 'spec-table__head';
    [
      ['product', t('col_product', 'Product')],
      ['price', t('col_price', 'Price')],
      ['qty', t('col_quantity', 'Quantity')],
      ['sub', t('col_subtotal', 'Subtotal')],
    ].forEach(function (col) {
      var cell = document.createElement('span');
      cell.className = 'spec-table__h spec-table__h--' + col[0];
      cell.textContent = col[1];
      head.appendChild(cell);
    });
    table.appendChild(head);

    groups.forEach(function (group) {
      if (!group.keys || !group.keys.length) return;
      if (group.title) {
        var heading = document.createElement('h4');
        heading.className = 'spec-table__group';
        heading.textContent = group.title;
        table.appendChild(heading);
      }
      group.keys.forEach(function (key) {
        if (store.line(key)) table.appendChild(buildRow(store, key));
      });
    });

    container.appendChild(table);
  }

  /**
   * Clicking anywhere else — or Escape — shuts an open size panel, the way a dropdown is expected
   * to behave. ONE handler for the page, not one per picker: the table is rebuilt on every change,
   * and a listener minted per row per redraw would pile up against panels long gone from the
   * document (same trap as the builder's «click outside», see assets/setup-builder.js).
   */
  function closeVariantPanels(event) {
    var open = document.querySelectorAll('.spec-var__panel:not([hidden])');
    Array.prototype.forEach.call(open, function (panel) {
      var wrap = panel.parentNode;
      if (event && event.type === 'click' && wrap && wrap.contains(event.target)) return;
      panel.hidden = true;
      var toggle = wrap && wrap.querySelector('.spec-var__toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      // …and it must not come back on the next redraw.
      var row = panel.closest('[data-spec-key]');
      if (row) {
        Object.keys(varOpen).forEach(function (entry) {
          if (entry.indexOf('|' + row.getAttribute('data-spec-key')) !== -1) varOpen[entry] = false;
        });
      }
    });
  }

  document.addEventListener('click', closeVariantPanels);
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeVariantPanels(null);
  });

  window.BQSpecTable = {
    render: render,
    totalLabel: totalLabel,
    unitPrice: unitPrice,
    lineTotal: lineTotal,
  };
})();
